import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { z } from 'zod/v4';
import type {
  DefaultColumn,
  FilterConfig,
  HistoryColumnsStore,
  SortConfig,
} from './history-columns-store.types';

const STORAGE_KEY = 'history-columns-config:v2';
const LEGACY_STORAGE_KEY = 'history-columns-config';

const defaultColumns: DefaultColumn[] = [
  { id: 'titulo', name: 'Titulo', field: 'titulo', sortable: true, visible: true },
  { id: 'processDefinitionName', name: 'Proceso', field: 'processDefinitionName', sortable: true, visible: true },
  { id: 'processInstanceTitle', name: 'Título', field: 'processInstanceTitle', sortable: true, visible: true },
  { id: 'taskName', name: 'Tarea Actual', field: 'taskName', sortable: true, visible: true },
  { id: 'assignee', name: 'Usuario Asignado', field: 'assignee', sortable: true, visible: true },
  { id: 'startTime', name: 'Fecha Inicio', field: 'startTime', sortable: true, visible: true },
  { id: 'endTime', name: 'Fecha Fin', field: 'endTime', sortable: true, visible: true },
  { id: 'state', name: 'Estado', field: 'state', sortable: true, visible: true },
  { id: 'initiator', name: 'Iniciador', field: 'initiator', sortable: false, visible: true },
  { id: 'accessReasons', name: 'Acceso', field: 'accessReasons', sortable: false, visible: true },
];

const defaultSortConfig: SortConfig = {
  field: 'startTime',
  direction: 'desc',
};

const defaultFilterConfig: FilterConfig = {
  variableFilter: '',
  dateFrom: null,
  dateTo: null,
  activePreset: null,
};

const defaultColumnOrder = defaultColumns.map((column) => column.id);
const defaultColumnIds = new Set(defaultColumnOrder);

const storedHistoryColumnsConfigSchema = z.looseObject({
  columnOrder: z.array(z.string()).optional(),
  sortConfig: z.object({
    field: z.string(),
    direction: z.enum(['asc', 'desc']),
  }).optional(),
  filterConfig: z.object({
    variableFilter: z.string().optional(),
    dateFrom: z.string().nullable().optional(),
    dateTo: z.string().nullable().optional(),
    activePreset: z.string().nullable().optional(),
    dateFormat: z.string().nullable().optional(),
  }).optional(),
});

function normalizeColumnOrder(value: string[] | undefined): string[] {
  const savedOrder = value?.filter((id) => defaultColumnIds.has(id)) ?? [];
  const savedIds = new Set(savedOrder);
  return [
    ...savedOrder,
    ...defaultColumnOrder.filter((id) => !savedIds.has(id)),
  ];
}

const useHistoryColumnsStore = create<HistoryColumnsStore>()(devtools(
  (set, get) => ({
    columnOrder: defaultColumnOrder,
    sortConfig: defaultSortConfig,
    filterConfig: defaultFilterConfig,
    error: null,

    setColumnOrder: (columnIds) => {
      set({ columnOrder: normalizeColumnOrder(columnIds) });
      get().saveToLocalStorage();
    },
    moveColumn: (fromIndex, toIndex) => {
      const columnOrder = [...get().columnOrder];
      const [movedColumn] = columnOrder.splice(fromIndex, 1);
      if (!movedColumn) return;
      columnOrder.splice(toIndex, 0, movedColumn);
      set({ columnOrder });
      get().saveToLocalStorage();
    },
    setSortConfig: (field, direction) => {
      set({ sortConfig: { field, direction } });
      get().saveToLocalStorage();
    },
    clearSort: () => {
      set({ sortConfig: defaultSortConfig });
      get().saveToLocalStorage();
    },
    setVariableFilter: (variableFilter) => {
      set({ filterConfig: { ...get().filterConfig, variableFilter } });
      get().saveToLocalStorage();
    },
    setDateFilter: (dateFrom, dateTo, activePreset = null) => {
      set({
        filterConfig: {
          ...get().filterConfig,
          dateFrom: dateFrom ?? null,
          dateTo: dateTo ?? null,
          activePreset: activePreset ?? null,
        },
      });
      get().saveToLocalStorage();
    },
    clearFilter: () => {
      set({ filterConfig: defaultFilterConfig });
      get().saveToLocalStorage();
    },
    loadFromLocalStorage: () => {
      try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return;
        const config = storedHistoryColumnsConfigSchema.parse(JSON.parse(stored));
        const storedFilter = config.filterConfig;
        set({
          columnOrder: normalizeColumnOrder(config.columnOrder),
          sortConfig: config.sortConfig ?? defaultSortConfig,
          filterConfig: {
            ...defaultFilterConfig,
            variableFilter: storedFilter?.variableFilter ?? defaultFilterConfig.variableFilter,
            dateFrom: storedFilter?.dateFrom ?? storedFilter?.dateFormat ?? null,
            dateTo: storedFilter?.dateTo ?? null,
            activePreset: storedFilter?.activePreset ?? null,
          },
        });
      } catch (error) {
        console.error('Error loading history columns config from localStorage:', error);
        set({ error: 'Error cargando configuración de columnas' });
      }
    },
    saveToLocalStorage: () => {
      try {
        const { columnOrder, sortConfig, filterConfig } = get();
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ columnOrder, sortConfig, filterConfig }),
        );
      } catch (error) {
        console.error('Error saving history columns config to localStorage:', error);
        set({ error: 'Error guardando configuración de columnas' });
      }
    },
    resetToDefaults: () => {
      set({
        columnOrder: defaultColumnOrder,
        sortConfig: defaultSortConfig,
        filterConfig: defaultFilterConfig,
        error: null,
      });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    },
  }),
  { name: 'history-columns-store' },
));

export const useColumnOrder = () => useHistoryColumnsStore((state) => state.columnOrder);
export const useSortConfig = () => useHistoryColumnsStore((state) => state.sortConfig);
export const useSetColumnOrder = () => useHistoryColumnsStore((state) => state.setColumnOrder);
export const useSetSortConfig = () => useHistoryColumnsStore((state) => state.setSortConfig);
export const useLoadFromLocalStorage = () => useHistoryColumnsStore((state) => state.loadFromLocalStorage);

export { defaultColumns };
export type {
  SortConfig,
  HistoryColumnsStore,
} from './history-columns-store.types';
